import { getTenantById } from 'tenants';
import type { TenantId } from 'tenants/types/tenant';

/**
 * Options for generating localized URLs
 */
export interface LocalizedUrlOptions {
  /** The locale code to use (e.g., 'en-CA', 'fr-CA') */
  locale: string;
  /** The tenant ID to get default locale from */
  tenantId: TenantId;
  /** The URL path (e.g., '/toronto-on-real-estate' or '/search?city=toronto') */
  path: string;
}

/**
 * Centralized utility to generate URLs with locale prefixes.
 * 
 * This function handles the logic of when to include locale in URLs:
 * - If locale is the tenant's default language: NO prefix (clean URLs)
 * - If locale is NOT the default language: Include prefix (e.g., /fr-CA/path)
 */
export function getLocalizedUrl(options: LocalizedUrlOptions): string {
  const { locale, tenantId, path } = options;
  const prefix = getLocalePrefix({ locale, tenantId });
  
  // Ensure path starts with '/'
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  return `${prefix}${normalizedPath}`;
}

/**
 * Get just the locale prefix for a given locale and tenant.
 * Returns empty string if locale is the tenant's default.
 * 
 * Normalizes English locale variants ('en' and 'en-CA') to be treated as equivalent.
 * This handles the case where Next.js i18n routing uses 'en' but tenant default is 'en-CA' (or vice versa).
 */
export function getLocalePrefix(options: Pick<LocalizedUrlOptions, 'locale' | 'tenantId'>): string {
  const { locale, tenantId } = options;
  const tenant = getTenantById(tenantId);
  const defaultLanguage = tenant.localization.defaultLanguage;

  // If locale is the default, return empty string
  if (locale === defaultLanguage) {
    return '';
  }

  // Normalize English locale variants
  // Both 'en' and 'en-CA' should be treated as the default (no prefix)
  const normalizedLocale = locale?.toLowerCase();
  const normalizedDefault = defaultLanguage?.toLowerCase();
  const isEnglishLocale = normalizedLocale === 'en' || normalizedLocale === 'en-ca';
  const isEnglishDefault = normalizedDefault === 'en' || normalizedDefault === 'en-ca';
  
  if (isEnglishLocale && isEnglishDefault) {
    return '';
  }

  // Return the locale prefix
  return `/${locale}`;
}

/**
 * Check if a locale should be included in URLs for a given tenant.
 * 
 */
export function shouldIncludeLocaleInUrl(options: Pick<LocalizedUrlOptions, 'locale' | 'tenantId'>): boolean {
  const { locale, tenantId } = options;
  const tenant = getTenantById(tenantId);
  return locale !== tenant.localization.defaultLanguage;
}

/**
 * Remove locale prefix from a path if it matches one of the supported languages.
 * 
 * Examples:
 * - '/en-CA/toronto-homes' with languages ['en-CA', 'fr-CA'] → '/toronto-homes'
 * - '/fr-CA/toronto-homes' with languages ['en-CA', 'fr-CA'] → '/toronto-homes'
 * - '/toronto-homes' with languages ['en-CA', 'fr-CA'] → '/toronto-homes' (unchanged)
 */
export function removeLocalePrefix(
  path: string,
  supportedLanguages: string[]
): string {
  // Iterate through each supported language and check if path starts with that prefix
  // Example: if path = '/fr-CA/toronto-homes' and supportedLanguages = ['en-CA', 'fr-CA']
  for (const lang of supportedLanguages) {
    // e.g., lang = 'en-CA' → prefixWithSlash = '/en-CA/'
    const prefixWithSlash = `/${lang}/`;
    // e.g., lang = 'en-CA' → justPrefix = '/en-CA'
    const justPrefix = `/${lang}`;
    
    // Check if path starts with '/lang/' (e.g., '/fr-CA/...')
    // path '/fr-CA/toronto-homes' starts with '/fr-CA/' → true
    if (path.startsWith(prefixWithSlash)) {
      // Remove the prefix: '/fr-CA/toronto-homes'.slice(6) → '/toronto-homes'
      // lang.length + 1 = 6 (accounts for '/' + 'fr-CA' + '/')
      return path.slice(lang.length + 1);
    }
    // Check if path is exactly the language prefix (e.g., '/fr-CA')
    if (path === justPrefix) {
      return '/';
    }
  }
  // If no language prefix matched, return path unchanged
  // Example: path = '/toronto-homes' → '/toronto-homes'
  return path;
}

